import { CSSProperties, useEffect, useRef } from "react";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  CampaignType,
  Offer,
  SingleSectionSettings,
  Template,
  TemplateOptionSettings,
  TextAlign,
  normalizeSectionSettings,
} from "../../../../types/global.types";
import FeaturedOfferSmall from "../../components/FeaturedSlots/FeaturedOfferSmall/FeaturedOfferSmall";
import FeaturedForMultiple from "../../components/FeaturedSlots/FeaturedForMultiple/FeaturedForMultiple";
import GridOffers, {
  getSectionGridTemplateColumns,
} from "../grid/GridOffers.component";
import { getDisplayedSectionOffers } from "../utils/sectionDisplay.utils";

export interface DynamicOfferActions {
  sessionId: string;
  displayOnly: boolean;
  campaign: CampaignType;
  campaignId: string;
  placementId: string;
  placementName: string;
  settingsTemplate: Template;
  layoutId: string;
  preEmail: string | undefined;
  setPreEmail?: (email: string) => void;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  canAutoSendEmailFromLabel: boolean;
  canAutoSendEmailFromMainCTA: boolean;
  onAutoSendEmailFromLabel: (offer: Offer, slot: number) => Promise<void>;
  autoSendingOfferId: string;
  isSeen: boolean;
}

interface Props {
  settings: TemplateOptionSettings;
  offers: Offer[];
  actions: DynamicOfferActions;
  isMobile?: boolean;
}

const getSectionHeaderAlignItems = (
  align: string
): CSSProperties["alignItems"] => {
  if (align === TextAlign.right) return "flex-end";
  if (align === TextAlign.center) return "center";
  return "flex-start";
};

const SectionFeaturedSwiper = ({
  featuredOffers,
  settings,
  actions,
  isMobile,
}: {
  featuredOffers: Offer[];
  settings: TemplateOptionSettings;
  actions: DynamicOfferActions;
  isMobile?: boolean;
}) => {
  const refNavPrev = useRef<HTMLDivElement | null>(null);
  const refNavNext = useRef<HTMLDivElement | null>(null);
  const refPagination = useRef<HTMLDivElement | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const hasAnyVoucherWithType = featuredOffers.some(
    (item) => Boolean(item.voucher?.voucherType)
  );

  const renderFeaturedOffer = (offer: Offer, index: number) => (
    <div className="preview-selected-offer">
      {isMobile ? (
        <FeaturedForMultiple
          offer={offer}
          slot={index + 1}
          settings={settings}
          settingsTemplate={actions.settingsTemplate}
          displayOnly={actions.displayOnly}
          isAdded={offer.isAdded || false}
          retailerId={actions.campaign.company_Id}
          campaignId={actions.campaignId}
          placementId={actions.placementId}
          placementName={actions.placementName}
          campaign={actions.campaign}
          preEmail={actions.preEmail}
          setPreEmail={actions.setPreEmail}
          openSingle={actions.openSingle}
          onAdd={actions.onAdd}
          hasVoucher={Boolean(offer.voucher?.voucherType)}
          hasAnyVoucherWithType={hasAnyVoucherWithType}
          offerLength={actions.campaign.offers.length}
          otherSettings={settings.otherSettings}
          layoutId={actions.layoutId}
          layoutOptions={settings.layoutOptions}
          canAutoSendEmailFromLabel={actions.canAutoSendEmailFromLabel}
          canAutoSendEmailFromMainCTA={actions.canAutoSendEmailFromMainCTA}
          onAutoSendEmailFromLabel={actions.onAutoSendEmailFromLabel}
          autoSendingOfferId={actions.autoSendingOfferId}
          isSeen={actions.isSeen}
        />
      ) : (
        <FeaturedOfferSmall
          offer={offer}
          slot={index + 1}
          settings={settings.dealLayout}
          mainSettings={settings.mainLayout}
          settingsTemplate={actions.settingsTemplate}
          displayOnly={actions.displayOnly}
          isAdded={offer.isAdded || false}
          retailerId={actions.campaign.company_Id}
          campaignId={actions.campaignId}
          placementId={actions.placementId}
          placementName={actions.placementName}
          campaign={actions.campaign}
          defaultOpen={false}
          preEmail={actions.preEmail}
          sessionId={actions.sessionId}
          setPreEmail={actions.setPreEmail}
          openSingle={actions.openSingle}
          onAdd={actions.onAdd}
          hasVoucher={Boolean(offer.voucher?.voucherType)}
          offerLength={actions.campaign.offers.length}
          otherSettings={settings.otherSettings}
          layoutId={actions.layoutId}
          layoutOptions={settings.layoutOptions}
          canAutoSendEmailFromLabel={actions.canAutoSendEmailFromLabel}
          canAutoSendEmailFromMainCTA={actions.canAutoSendEmailFromMainCTA}
          onAutoSendEmailFromLabel={actions.onAutoSendEmailFromLabel}
          autoSendingOfferId={actions.autoSendingOfferId}
          openEmailInPopup
        />
      )}
    </div>
  );

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      if (!swiperRef.current) return;

      if (refNavPrev.current && refNavNext.current) {
        swiperRef.current.params.navigation = {
          ...(swiperRef.current.params.navigation as object),
          prevEl: refNavPrev.current,
          nextEl: refNavNext.current,
        };
        swiperRef.current.navigation.destroy();
        swiperRef.current.navigation.init();
        swiperRef.current.navigation.update();
      }

      if (refPagination.current) {
        swiperRef.current.params.pagination = {
          ...(swiperRef.current.params.pagination as object),
          clickable: true,
          el: refPagination.current,
        };
        swiperRef.current.pagination.destroy();
        swiperRef.current.pagination.init();
        swiperRef.current.pagination.render();
        swiperRef.current.pagination.update();
      }

      swiperRef.current.update();
    });

    return () => cancelAnimationFrame(frameId);
  }, [featuredOffers.length]);

  if (featuredOffers.length === 0) {
    return null;
  }

  return (
    <div
      className={`preview-section-featured preview-section-featured-swiper${featuredOffers.length === 1 ? " is-single-slide" : ""
        }${isMobile ? " is-mobile" : ""}`}
      style={
        {
          "--swiper-pagination-color": settings.mainLayout.textColor,
          "--swiper-pagination-bullet-inactive-color": `${settings.mainLayout.textColor}59`,
          "--featured-peek-fade": settings.mainLayout.backgroundColor || "#ffffff",
        } as CSSProperties
      }
    >
      <Swiper
        className="swiper-container"
        modules={[Navigation, Pagination]}
        watchSlidesProgress={true}
        slidesPerView="auto"
        centeredSlides={featuredOffers.length > 1 ? false : true}
        spaceBetween={12}
        pagination={{
          clickable: true,
          el: refPagination.current || undefined,
        }}
        navigation={{
          prevEl: refNavPrev.current,
          nextEl: refNavNext.current,
        }}
        onBeforeInit={(swiper: SwiperType) => {
          const nav = swiper.params.navigation;
          if (nav && typeof nav !== "boolean") {
            nav.prevEl = refNavPrev.current;
            nav.nextEl = refNavNext.current;
          }
          const pagination = swiper.params.pagination;
          if (pagination && typeof pagination !== "boolean") {
            pagination.el = refPagination.current;
          }
        }}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper;
        }}
      >
        {featuredOffers.map((offer, index) => (
          <SwiperSlide key={offer.id}>
            <div className="preview-featured-slide-offer">
              {renderFeaturedOffer(offer, index)}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {featuredOffers.length > 1 &&
        <div className="dynamic-section-navigation">
          <div className="swiper-button-prev" ref={refNavPrev}>
            <svg
              width="34"
              height="34"
              viewBox="0 0 34 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="17"
                cy="17"
                r="17"
                transform="rotate(-180 17 17)"
                fill={settings.dealLayout.backgroundColor}
              />
              <rect
                x="18.6094"
                y="23"
                width="8.6192"
                height="1.72384"
                rx="0.86192"
                transform="rotate(-135 18.6094 23)"
                fill={settings.dealLayout.textColor}
              />
              <rect
                x="20.3135"
                y="12.2188"
                width="8.6192"
                height="1.72384"
                rx="0.86192"
                transform="rotate(135 20.3135 12.2188)"
                fill={settings.dealLayout.textColor}
              />
            </svg>
          </div>

          <div className="swiper-pagination-super" ref={refPagination}></div>

          <div className="swiper-button-next" ref={refNavNext}>
            <svg
              width="34"
              height="34"
              viewBox="0 0 34 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="17"
                cy="17"
                r="17"
                fill={settings.dealLayout.backgroundColor}
              />
              <rect
                x="15.3906"
                y="11"
                width="8.6192"
                height="1.72384"
                rx="0.86192"
                transform="rotate(45 15.3906 11)"
                fill={settings.dealLayout.textColor}
              />
              <rect
                x="14.1719"
                y="22.2656"
                width="8.6192"
                height="1.72384"
                rx="0.86192"
                transform="rotate(-45 14.1719 22.2656)"
                fill={settings.dealLayout.textColor}
              />
            </svg>
          </div>
        </div>
      }
    </div>
  );
};

const PreviewSections = ({ settings, actions, isMobile }: Props) => {
  const sectionSettings = normalizeSectionSettings(settings.sections);
  const sections = sectionSettings.sections;
  const displayedBySection = getDisplayedSectionOffers(
    sections,
    actions.campaign.offers || []
  );

  return (
    <div className="preview-sections-shell is-preview-mode">
      <div
        className={`preview-sections${settings.filters?.filters ? " has-filters" : ""
          }${sectionSettings.topMenu.enabled ? " has-top-menu" : ""}${isMobile ? " is-mobile" : ""}`}
      >
        {sections.map((section: SingleSectionSettings, index: number) => {
          const { featuredOffers, displaySlotted } = displayedBySection[index];
          const sectionAlign = settings.mainLayout.align || TextAlign.center;
          const sectionTitle = section.title || "";
          const sectionDescription = section.description || "";

          if (featuredOffers.length === 0 && displaySlotted.length === 0) {
            return null;
          }

          return (
            <div
              className={`preview-section preview-section-${sectionAlign}`}
              id={`preview-section-${index}`}
              key={`preview-section-${index}`}
              style={{ textAlign: sectionAlign }}
            >
              {(sectionTitle !== "" || sectionDescription !== "") && (
                <div
                  className="preview-section-header"
                  style={{ alignItems: getSectionHeaderAlignItems(sectionAlign) }}
                >
                  {sectionTitle !== "" && (
                    <div
                      className="preview-section-title"
                      style={{
                        color: section.titleColor,
                        fontSize: settings.mainLayout.headerFontSize
                          ? `${settings.mainLayout.headerFontSize}px`
                          : undefined,
                        fontWeight: settings.mainLayout.headerFontWeight,
                      }}
                    >
                      {sectionTitle}
                    </div>
                  )}
                  {sectionDescription !== "" && (
                    <div
                      className="preview-section-description"
                      style={{
                        color: section.descriptionColor,
                        fontSize: settings.mainLayout.subHeaderFontSize
                          ? `${settings.mainLayout.subHeaderFontSize}px`
                          : undefined,
                        fontWeight: settings.mainLayout.subHeaderFontWeight,
                      }}
                    >
                      {sectionDescription}
                    </div>
                  )}
                </div>
              )}

              <SectionFeaturedSwiper
                featuredOffers={featuredOffers}
                settings={settings}
                actions={actions}
                isMobile={isMobile}
              />

              {displaySlotted.length > 0 && (
                <div className="inline-grid">
                  <GridOffers
                    sessionId={actions.sessionId}
                    offers={displaySlotted}
                    settings={settings}
                    campaign={actions.campaign}
                    displayOnly={actions.displayOnly}
                    offersCount={String(displaySlotted.length)}
                    settingsTemplate={actions.settingsTemplate}
                    campaignId={actions.campaignId}
                    placementId={actions.placementId}
                    placementName={actions.placementName}
                    openSingle={actions.openSingle}
                    onAdd={actions.onAdd}
                    setPreEmail={actions.setPreEmail}
                    preEmail={actions.preEmail}
                    isSeen={actions.isSeen}
                    layoutId={actions.layoutId}
                    canAutoSendEmailFromLabel={actions.canAutoSendEmailFromLabel}
                    canAutoSendEmailFromMainCTA={
                      actions.canAutoSendEmailFromMainCTA
                    }
                    onAutoSendEmailFromLabel={actions.onAutoSendEmailFromLabel}
                    autoSendingOfferId={actions.autoSendingOfferId}
                    columns={section.columns}
                    gridTemplateColumns={getSectionGridTemplateColumns(
                      section.columns
                    )}
                    offerCardSize={section.offerCardSize}
                    showLogo={section.showLogo}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PreviewSections;
